import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FireCertApplication, FireCertState, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['FIRE_INSPECTOR', 'CHIEF_FIRE_OFFICER'];
const STATES: FireCertState[] = ['APPLIED', 'DOC_REVIEW', 'INSPECTION_SCHEDULED', 'INSPECTED', 'RECTIFICATION', 'CERT_ISSUED', 'REJECTED', 'WITHDRAWN'];

/** Fire & Rescue — fire safety certificate applications: apply -> doc review -> inspection -> cert (M06). */
export function FireApplications() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m06/applications?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<FireCertApplication>>(path);

  return (
    <div>
      <PageHeader
        title="Fire Certificate Applications"
        subtitle="Apply -> doc review -> inspection booking -> findings/result -> certificate (M06)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-52" placeholder="Ref, entity, or address…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All states</option>
              {STATES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Entity</th>
                <th className="table-head">Premises</th>
                <th className="table-head">Cert status</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{a.refNo}</td>
                  <td className="table-cell text-xs text-slate-500">{a.entityId}</td>
                  <td className="table-cell text-xs text-slate-500">{a.premises?.address ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.certStatus} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <ApplicationDetailModal
          id={selectedId}
          canAct={isOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ApplicationDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<FireCertApplication>(`/api/v1/m06/applications/${id}`);
  const a = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approve, setApprove] = useState<boolean | null>(null);
  const [note, setNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const docReview = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m06/applications/${id}/doc-review`, {
        approve,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(!approve ? { rejectionReason: rejectionReason.trim() } : {}),
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to review');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={a ? `${a.refNo}` : 'Fire certificate application'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={a.state} />} />
            <Detail label="Fee" value={scr(a.feeAmountScr)} />
            <Detail label="Fee status" value={<StatusBadge status={a.feeStatus} />} />
            <Detail label="Cert status" value={<StatusBadge status={a.certStatus} />} />
            {a.certSerial ? <Detail label="Cert serial" value={a.certSerial} mono /> : null}
            {a.certExpiresAt ? <Detail label="Cert expires" value={formatDate(a.certExpiresAt)} /> : null}
            {a.rectificationDueAt ? <Detail label="Rectification due" value={formatDate(a.rectificationDueAt)} /> : null}
          </div>

          {a.premises ? (
            <div>
              <div className="field-label">Premises</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Detail label="Address" value={a.premises.address} />
                <Detail label="District" value={a.premises.district} />
                <Detail label="Use type" value={a.premises.useType} />
                <Detail label="Floor area" value={`${a.premises.floorAreaSqm} m²`} />
                <Detail label="Occupancy load" value={String(a.premises.occupancyLoad)} />
              </div>
            </div>
          ) : null}

          {a.rejectionReason ? <ErrorNote message={`Rejected: ${a.rejectionReason}`} /> : null}
          {a.docReviewNote ? <Detail label="Doc review note" value={`${a.docReviewNote} (${a.docReviewedBy})`} /> : null}

          {a.inspections && a.inspections.length > 0 ? (
            <div>
              <div className="field-label">Inspection history</div>
              <div className="space-y-1.5 text-sm">
                {a.inspections.map((insp) => (
                  <div key={insp.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5">
                    <span>
                      {insp.type} — {formatDate(insp.scheduledFor)}
                    </span>
                    <div className="flex items-center gap-2">
                      {insp.result ? <StatusBadge status={insp.result} /> : null}
                      <StatusBadge status={insp.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {canAct && a.state === 'DOC_REVIEW' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Doc review decision</div>
              <div className="flex gap-2">
                <button className={`btn-success ${approve === true ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(true)}>
                  Approve drawings
                </button>
                <button className={`btn-danger ${approve === false ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(false)}>
                  Reject drawings
                </button>
              </div>
              {approve !== null ? (
                <>
                  <Field label="Note (optional)">
                    <input className="field-input" value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  {!approve ? (
                    <Field label="Rejection reason (required)">
                      <textarea className="field-input" rows={2} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
                    </Field>
                  ) : null}
                  <button
                    className={approve ? 'btn-success' : 'btn-danger'}
                    disabled={busy || (!approve && rejectionReason.trim().length < 3)}
                    onClick={docReview}
                  >
                    {busy ? 'Submitting…' : `Confirm ${approve ? 'approval' : 'rejection'}`}
                  </button>
                </>
              ) : null}
            </div>
          ) : canAct ? null : a.state === 'DOC_REVIEW' ? (
            <RoleNotice roles={OFFICER_ROLES} />
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Applied {formatDateTime(a.createdAt)} · last updated {formatDateTime(a.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
